
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import '../Style/RollOut.css';

const API_BASE_URL = "https://api.yellowmatrix.co.in"; 

const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("No token found");
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers };
  const response = await fetch(`${API_BASE_URL}${url}`, { ...options, headers });
  const data = await response.json();
  if (!response.ok) throw new Error(data.Error || data.msg || "API Error");
  return data;
};

const useAvailableVehiclesQuery = () => {
  return useQuery({
    queryKey: ['availableVehicles'],
    queryFn: async () => {
      const data = await fetchWithAuth('/vehicle/device/assign');
      return data.Vehicles || [];
    },
    staleTime: 30000, 
  });
};

const RollOut = ({ onBackToList }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const retryData = location.state?.retryData || null;

  const [rolloutData, setRolloutData] = useState({
    rolloutName: "", deviceGroup: "", url: "", customerId: "", version: "", crc32: "", targetaddress:""
  });
  
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // States for Vehicle Search
  const [vehicleInput, setVehicleInput] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);

  // States for Customer Selection
  const [customerInput, setCustomerInput] = useState("");
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const customerRef = useRef(null);

  const { data: vehicles = [], isLoading: fetchingVehicles } = useAvailableVehiclesQuery();

  // 1. Get unique Customer IDs from the vehicles list
  const uniqueCustomers = useMemo(() => {
    const customers = vehicles.map(v => String(v.Customerid)).filter(Boolean);
    return [...new Set(customers)];
  }, [vehicles]);

  // 2. Filter Customers based on input
  const filteredCustomers = useMemo(() => {
    return uniqueCustomers.filter(c => c.toLowerCase().includes(customerInput.toLowerCase()));
  }, [customerInput, uniqueCustomers]);

  // 3. Filter Vehicles based on Search + Selected Customer
  useEffect(() => {
    if (!vehicles) return;
    const lowerSearch = vehicleInput.toLowerCase();
    const currentCustomer = rolloutData.customerId.trim();

    const filtered = vehicles.filter(v => {
      const matchesSearch = 
        (v.VehicleID && v.VehicleID.toLowerCase().includes(lowerSearch)) || 
        (v.DeviceID && v.DeviceID.toLowerCase().includes(lowerSearch));

      // Strictly filter by the selected Customer ID
      const matchesCustomer = currentCustomer ? String(v.Customerid) === currentCustomer : false; 

      return matchesSearch && matchesCustomer;
    });

    setFilteredSuggestions(filtered);
  }, [vehicleInput, vehicles, rolloutData.customerId]);

  // Click Outside logic for both dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => { 
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) setShowSuggestions(false); 
      if (customerRef.current && !customerRef.current.contains(event.target)) setShowCustomerSuggestions(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (field, value) => setRolloutData(prev => ({ ...prev, [field]: value }));

  const handleCustomerSelect = (custId) => {
    handleChange('customerId', custId);
    setCustomerInput(custId);
    setShowCustomerSuggestions(false);
    // Clear selected vehicles if customer changes
    setSelectedVehicles([]);
  };

  const handleVehicleSelect = (vehicleObj) => {
    if (!selectedVehicles.find(v => v.DeviceID === vehicleObj.DeviceID)) {
      setSelectedVehicles(prev => [...prev, vehicleObj]);
    }
    setVehicleInput(""); 
    setShowSuggestions(false);
  };

  const removeVehicle = (deviceID) => setSelectedVehicles(prev => prev.filter(v => v.DeviceID !== deviceID));

  const handleCreateRollout = async () => {
    if (retryData) {
        if (!rolloutData.url) return alert("Please enter new URL");
        setIsSubmitting(true);
        try {
            const fotaId = retryData.id || retryData.ecu_fota_id;
            await fetchWithAuth(`/ecu-fota/trigger/${fotaId}`, {
                method: "POST",
                body: JSON.stringify({ link: rolloutData.url })
            });
            alert("Rollout restarted successfully!");
            navigate(-1);
        } catch (err) { alert(err.message); }
        finally { setIsSubmitting(false); }
        return;
    }

    if (selectedVehicles.length === 0) return alert("Please select vehicles");
    setIsSubmitting(true);
    try {
      const createRes = await fetchWithAuth('/ecu-fota/create', {
        method: "POST",
        body: JSON.stringify({
            customer_id: rolloutData.customerId,
            device_group: rolloutData.deviceGroup,
            firmware_name: rolloutData.rolloutName,
            version: rolloutData.version,
            devices: selectedVehicles.map(v => ({ device_id: v.DeviceID, crc32: rolloutData.crc32, bin_size: "1024", target_address: rolloutData.targetaddress }))
        })
      });
      await fetchWithAuth(`/ecu-fota/trigger/${createRes.ecu_fota_id}`, {
        method: "POST",
        body: JSON.stringify({ link: rolloutData.url })
      });
      alert("Rollout triggered successfully!");
      if (onBackToList) onBackToList(); else navigate(-1);
    } catch (error) { alert(error.message); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="Rollout-ECURollOut">
      <div className="Form-container-ECURollOut">
        <div className="header-ECURollOut">
          <h1 className="Form-header-ECURollOut">
            {retryData ? `Retry Rollout: ${retryData.deviceGroup}` : "Create New Rollout"}
          </h1>
        </div>

        <div className="Form-section-ECURollOut">
          <h2 className="Form-section-title-ECURollOut">
            {retryData ? "Update Firmware Link" : "Firmware Details"}
          </h2>
          
          <div className="Form-card-ECURollOut Form-grid-ECURollOut">
            {retryData ? (
                <div className="Form-field-ECURollOut" style={{ gridColumn: '1 / -1' }}>
                    <label className="Form-label-ECURollOut">* New Package URL</label>
                    <input type="text" className="Form-input-ECURollOut" 
                        value={rolloutData.url} 
                        onChange={(e) => handleChange('url', e.target.value)} 
                        placeholder="Enter the new firmware link here..." />
                </div>
            ) : (
                <>
                <div className="Form-field-ECURollOut">
                    <label className="Form-label-ECURollOut">* Firmware Name</label>
                    <input type="text" className="Form-input-ECURollOut" value={rolloutData.rolloutName} onChange={(e) => handleChange('rolloutName', e.target.value)} placeholder="Firmware Name" />
                </div>
                <div className="Form-field-ECURollOut">
                    <label className="Form-label-ECURollOut">* Device Group</label>
                    <input type="text" className="Form-input-ECURollOut" value={rolloutData.deviceGroup} onChange={(e) => handleChange('deviceGroup', e.target.value)} placeholder="Device Group" />
                </div>
                </>
            )}
          </div>
        </div>

        {!retryData && (
          <>
            <div className="Form-section-ECURollOut">
              <h2 className="Form-section-title-ECURollOut">Package Information</h2>
              <div className="Form-card-ECURollOut Form-grid-ECURollOut">
                <div className="Form-field-ECURollOut">
                  <label className="Form-label-ECURollOut">* URL</label>
                  <input type="text" className="Form-input-ECURollOut" value={rolloutData.url} onChange={(e) => handleChange('url', e.target.value)} placeholder="URL" />
                </div>
              </div>
            </div>

            <div className="Form-section-ECURollOut">
              <h2 className="Form-section-title-ECURollOut">Vehicle Info</h2>
              <div className="Form-card-ECURollOut Form-grid-ECURollOut">
                
                {/* --- Selectable Customer ID Dropdown --- */}
                <div className="Form-field-ECURollOut" ref={customerRef} style={{ position: 'relative' }}>
                  <label className="Form-label-ECURollOut">* Select Customer ID</label>
                  <input 
                    type="text" 
                    className="Form-input-ECURollOut" 
                    placeholder="Search or select Customer..."
                    value={customerInput}
                    onChange={(e) => setCustomerInput(e.target.value)}
                    onFocus={() => setShowCustomerSuggestions(true)}
                  />
                  {showCustomerSuggestions && (
                    <ul className="firmware-suggestions-list-ECURollOut" style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, 
                      background: '#fff', border: '1px solid #ccc', zIndex: 11, 
                      maxHeight: '150px', overflowY: 'auto', listStyle: 'none', 
                      padding: 0, margin: 0, borderRadius: '4px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                      {filteredCustomers.length > 0 ? (
                        filteredCustomers.map((custId, idx) => (
                          <li key={idx} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #eee' }} 
                              onClick={() => handleCustomerSelect(custId)}>
                            {custId}
                          </li>
                        ))
                      ) : (
                        <li style={{ padding: '10px', color: '#94a3b8' }}>No customers found</li>
                      )}
                    </ul>
                  )}
                </div>

                <div className="Form-field-Rollout-ECURollOut">
                  <label className="Form-label-ECURollOut">* Version</label>
                  <input type="text" className="Form-input-ECURollOut" value={rolloutData.version} onChange={(e) => handleChange('version', e.target.value)} placeholder="Enter Version" />
                </div>
                <div className="Form-field-ECURollOut">
                  <label className="Form-label-ECURollOut">* CRC32</label>
                  <input type="text" className="Form-input-ECURollOut" value={rolloutData.crc32} onChange={(e) => handleChange('crc32', e.target.value)} placeholder="Enter CRC32" />
                </div>
                <div className="Form-field-ECURollOut">
                  <label className="Form-label-ECURollOut">* Target Address</label>
                  <input type="text" className="Form-input-ECURollOut" value={rolloutData.targetaddress} onChange={(e) => handleChange('targetaddress', e.target.value)} placeholder="Enter Target Address" />
                </div>
              </div>
            </div>

            <div className="Form-section-ECURollOut">
              <h2 className="Form-section-title-ECURollOut">Vehicle Selection ({selectedVehicles.length} selected)</h2>
              <div className="Form-card-ECURollOut">
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                  {selectedVehicles.map(v => (
                    <div key={v.DeviceID} style={{ background: '#e0e7ff', color: '#3730a3', padding: '4px 10px', borderRadius: '16px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {v.VehicleID}
                      <span onClick={() => removeVehicle(v.DeviceID)} style={{ cursor: 'pointer', fontWeight: 'bold' }}>×</span>
                    </div>
                  ))}
                </div>

                <div className="Form-field-ECURollOut field-width-50-ECURollOut" ref={searchInputRef} style={{ position: 'relative' }}>
                  <label className="Form-label-ECURollOut">* Select Vehicles</label>
                  <input 
                    type="text" 
                    className="Form-input-ECURollOut" 
                    placeholder={rolloutData.customerId ? "Search vehicles..." : "Select Customer ID above first"}
                    value={vehicleInput}
                    disabled={!rolloutData.customerId}
                    onChange={(e) => setVehicleInput(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    style={{ backgroundColor: !rolloutData.customerId ? '#f1f5f9' : '#fff' }}
                  />
                  
                  {showSuggestions && (
                    <ul className="firmware-suggestions-list-ECURollOut" style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, 
                      background: '#fff', border: '1px solid #ccc', zIndex: 10, 
                      maxHeight: '200px', overflowY: 'auto', listStyle: 'none', 
                      padding: 0, margin: 0, borderRadius: '4px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                      {fetchingVehicles ? (
                        <li style={{ padding: '10px' }}>Loading...</li>
                      ) : filteredSuggestions.length > 0 ? (
                        filteredSuggestions.map((v, idx) => (
                          <li key={idx} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #eee' }} 
                              onClick={() => handleVehicleSelect(v)}>
                            {v.VehicleID} <small style={{ color: '#666' }}>({v.DeviceID})</small>
                          </li>
                        ))
                      ) : (
                        <li style={{ padding: '10px', color: '#94a3b8' }}>No matching vehicles found</li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <div className="Form-actions-ECURollOut">
          <button className="Form-button-secondary-ECURollOut" onClick={() => navigate(-1)} disabled={isSubmitting}>Cancel</button>
          <button className="Form-button-primary-ECURollOut" onClick={handleCreateRollout} disabled={isSubmitting}>
            {isSubmitting ? "Processing..." : retryData ? "Restart Rollout" : "Create Rollout"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RollOut;